import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Inject,
  model,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { first, Subscription } from 'rxjs';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { FilterOption } from '@components/abb-table/abb-table.component';
import { categorySelectOptionsQuery } from '@graphql/category';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { Apollo } from 'apollo-angular';
import {
  CategorySelectOptionsQuery,
  CategorySelectOptionsQueryVariables,
  ConfigureDeviceMutation,
  ConfigureDeviceMutationVariables,
  Device,
  RequestFirmwareUpdateMutation,
  RequestFirmwareUpdateMutationVariables,
} from 'gql/graphql';
import { MatSelectModule } from '@angular/material/select';
import {
  configureDeviceMutation,
  requestFirmwareUpdateMutation,
} from '@graphql/commissioning';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';

type DeviceEditableFields = Pick<
  Device,
  'id' | 'imgUrl' | 'name' | 'status' | 'firmware'
> & {
  property: Pick<Device['property'], 'id' | 'name'>;
} & { category: Pick<Device['category'], 'id' | 'name'> } & {
  communicationProtocol: Pick<Device['communicationProtocol'], 'id' | 'name'>;
};

export type DeviceConfigurationModalData = {
  device: DeviceEditableFields;
};
@Component({
  selector: 'abb-device-configuration-modal',
  standalone: true,
  imports: [
    AbbModalComponent,
    CommonModule,
    TPipe,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    AbbButtonComponent,
    MatSelectModule,
  ],
  template: `
    <abb-modal [title]="'Configure device' | t" [closable]="!loading()">
      <div class="flex flex-col flex-1 pr-8">
        @if (data.device; as device) {

        <div class="flex flex-row gap-x-5">
          <div class="image-upload-container">
            @if(imagePreview){
            <div class="image-preview">
              <img
                [src]="imagePreview"
                alt="Image Preview"
                class="preview-image"
              />
            </div>
            }
          </div>
          <div class="flex-1 px-5 py-2 flex flex-col min-w-[400px]">
            <form class="flex flex-col flex-1 min-h-0" (ngSubmit)="save()">
              <div class="flex flex-col gap-4 flex-1 min-h-0 overflow-auto">
                <mat-form-field>
                  <mat-label>{{ 'Device' | t }}</mat-label>
                  <input
                    matInput
                    type="text"
                    name="deviceName"
                    [(ngModel)]="name"
                  />
                </mat-form-field>
                <mat-form-field>
                  <mat-label>{{ 'Status' | t }}</mat-label>
                  <input
                    matInput
                    type="text"
                    name="deviceStatus"
                    [(ngModel)]="status"
                    [readonly]="true"
                    [disabled]="true"
                  />
                </mat-form-field>
                <mat-form-field color="primary">
                  <mat-label>{{ 'Category' | t }}</mat-label>
                  <mat-select
                    [(ngModel)]="categoryId"
                    name="deviceCategory"
                    [panelClass]="'panel'"
                  >
                    @for (category of categorySelectOptions(); track
                    category.value) {
                    <mat-option [value]="category.value">{{
                      category.label | t
                    }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <mat-form-field>
                  <mat-label>{{ 'Property' | t }}</mat-label>
                  <input
                    matInput
                    type="text"
                    name="deviceProperty"
                    [(ngModel)]="property"
                    [readonly]="true"
                    [disabled]="true"
                  />
                </mat-form-field>
                <!-- @if(fwUpdateAvailable()){
                <abb-button
                  class="self-end"
                  [loading]="updatingFw()"
                  [disabled]="updatingFw()"
                  (onclick)="updateFw($event, device.id)"
                  >{{ 'Update firmware' | t }}</abb-button
                >
                } -->
              </div>
              <div class="flex justify-end items-center gap-3 mt-3 mb-1">
                <abb-button
                  type="button"
                  variant="secondary"
                  [disabled]="loading()"
                  (onclick)="modalRef()?.handleClose()"
                  >{{ 'cancel' | t }}</abb-button
                >
                <abb-button
                  type="submit"
                  [disabled]="loading()"
                  [loading]="loading()"
                  >{{ 'Save' | t }}</abb-button
                >
              </div>
            </form>
          </div>
        </div>
        }
      </div>
    </abb-modal>
  `,
  styles: `
    .image-upload-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: 1px dashed rgb(var(--abb-color-black) / 0.15);
      border-radius: 8px;
      width: 100%;
      height: 100%;
      min-height: 400px;
      width: 400px;
      text-align: center;
      cursor: pointer;
    }

    .image-upload-label {
      border: 1px solid rgb(var(--abb-color-black) / 0.15);
      color: rgb(var(--abb-color-black) / 0.30);
      align-items: center;
      justify-content: center;
      padding: 10px;
      border-radius: 4px;
      cursor: pointer;
    }

    input[type="file"] {
      display: none;
    }

    .image-preview {
      width: 100%;
      height: 100%;
      flex: 1;
      position: relative;
      overflow: hidden;
    }

    .preview-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      display: block;
    }
  `,
  host: { class: 'flex flex-1 min-h-0 flex-col' },
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'wizard_first_access',
    },
  ],
})
export class DeviceConfigurationModalComponent
  implements ModalData<DeviceConfigurationModalData>, OnDestroy
{
  private prefix = inject(TRANSLATION_PREFIX);
  modalRef = viewChild(AbbModalComponent);
  loading = signal(true);
  categorySelectOptions = signal<FilterOption[]>([]);
  imagePreview: string | ArrayBuffer | null = null;

  fwUpdateAvailable = signal(false);
  imageUrl = model<string | null>(null);
  name = model('');
  status = model('');
  categoryId = model<string | null>(null);
  property = model('');

  private subscriptions: Subscription[] = [];
  constructor(
    @Inject(MODAL_DATA) public data: DeviceConfigurationModalData,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService
  ) {
    this.loading.set(false);
    this.subscriptions.push(
      apollo
        .watchQuery<
          CategorySelectOptionsQuery,
          CategorySelectOptionsQueryVariables
        >({ query: categorySelectOptionsQuery })
        .valueChanges.subscribe({
          next: (res) => {
            this.categorySelectOptions.set(
              res.data.categoryList.map((cat) => ({
                value: cat.id,
                label: cat.name,
                skipTranslation: true,
              }))
            );
            this.categoryId.set(
              (this.categorySelectOptions()?.find(
                (c) => c.value === data.device.category.id
              )?.value as string) ?? ''
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
    // TODO: capire come gestire la preview dell'immagine
    //this.imagePreview = data.device.imgUrl;
    this.imageUrl.set(data.device.imgUrl);
    if (data.device.imgUrl) this.imagePreview = data.device.imgUrl;

    this.name.set(data.device.name);
    this.status.set(data.device.status);
    this.property.set(data.device.property.name);
    this.fwUpdateAvailable.set(data.device.firmware.updateAvailable);
  }

  updatingFw = signal(false);
  updateFw(event: Event, id: string): void {
    this.updatingFw.set(true);
    this.apollo
      .mutate<
        RequestFirmwareUpdateMutation,
        RequestFirmwareUpdateMutationVariables
      >({
        mutation: requestFirmwareUpdateMutation,
        variables: {
          devices: [{ id }],
        },
      })
      .subscribe({
        next: ({ data }) => {
          this.updatingFw.set(false);
          this.fwUpdateAvailable.set(
            data?.requestFirmwareUpdate[0].updateAvailable ?? false
          );
        },
        error: (err) => {
          console.error(err);
          this.updatingFw.set(false);
        },
      });
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
        const imageUrl = URL.createObjectURL(file);
        this.imageUrl.set(imageUrl);
      };
      reader.readAsDataURL(file);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  save() {
    this.loading.set(true);
    this.apollo
      .mutate<ConfigureDeviceMutation, ConfigureDeviceMutationVariables>({
        mutation: configureDeviceMutation,
        variables: {
          id: this.data.device.id,
          name: this.name(),
          imgUrl: this.imageUrl(),
          categoryId: this.categoryId(),
        },
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.modalRef()?.handleClose();
          this.loading.set(false);
          this.toast.notifySuccess(
            t(this.prefix, `Device configured successfully`, this.translate)
          );
        },
        error: () => {
          this.loading.set(false);
          this.toast.notifyError(
            t(
              this.prefix,
              `An error occurred while configuring device`,
              this.translate
            )
          );
        },
      });
  }
}
